import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Box, Button, TextField, Tooltip } from '@mui/material';
import { Close } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import ConfirmModal from './ConfirmModal';

/**
 * 라커 자리의 이용권 배치를 정하는 모달 (합치기 — 순서 큐).
 *
 * - 대상 자리의 기존 이용권과 옮기는 이용권을 한 줄씩 세우고, 손잡이를 끌어 순서를 바꾼다.
 * - 순서를 바꾸면 각 이용권의 이용 일수를 유지한 채 빈틈없이 다시 이어 붙는다(전체 시작일 고정).
 * - 자물쇠를 잠근 행은 날짜가 고정되고 재계산에서 빠진다. 나머지가 그 앞뒤로 붙는다.
 * - 겹침이 남아 있으면 확인이 비활성된다.
 *
 * onConfirm(entries) — entries: [{ memberLockerPassId, startDate: Date, expireDate: Date }]
 * 전체 배치를 한 번에 넘긴다(중간 상태 충돌을 피하려 서버도 배치로 확정).
 */
const ROW_TONE = {
  // 기존 이용권 — 옅은 파랑
  spot: { bg: '#F2F6FB', border: '1px solid #C7D9EC', accent: '#2F6FA8' },
  // 옮기는 이용권 — 주황
  moving: { bg: 'rgba(232,163,61,0.07)', border: '2px solid #E8A33D', accent: '#B97A16' },
  // 잠긴 행 — 회색 (이용중 카드 배경과 같은 톤)
  locked: { bg: '#F7F9FA', border: '1px solid #E3E8E9', accent: '#737373' },
};

const dateFieldSx = {
  flex: 1,
  '& .MuiOutlinedInput-root': { height: 38, borderRadius: '8px', fontSize: 13 },
};

const spanOf = (start, expire) =>
  dayjs(expire).startOf('day').diff(dayjs(start).startOf('day'), 'day') + 1;

const DragHandle = ({ color }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: 'inline-flex', flexDirection: 'column', gap: '2.5px', flexShrink: 0,
      p: '4px 2px', cursor: 'grab', '&:active': { cursor: 'grabbing' },
    }}>
    {[0, 1, 2].map(i => (
      <Box key={i} sx={{ width: 3.5, height: 3.5, borderRadius: '50%', background: color }} />
    ))}
  </Box>
);

const LockIcon = ({ locked, color }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={color} aria-hidden>
    {locked ? (
      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" />
    ) : (
      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h2c0-1.66 1.34-3 3-3s3 1.34 3 3v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2z" />
    )}
  </svg>
);

/**
 * 순서대로 다시 이어 붙인다.
 * 잠긴 행은 자기 기간을 지키는 기준점이고, 그 앞 구간은 잠금 시작일에서 역방향으로,
 * 뒤 구간은 잠금 종료일 다음 날부터 순방향으로 채운다.
 */
const rechain = (rows, anchorStart) => {
  const next = rows.map(row => ({ ...row }));
  const firstLocked = next.findIndex(row => row.locked);

  // 맨 앞 잠금 이전 구간 — 뒤에서부터 역방향
  if (firstLocked > 0) {
    let cursor = next[firstLocked].start;
    for (let i = firstLocked - 1; i >= 0; i -= 1) {
      const end = cursor.subtract(1, 'day');
      next[i] = { ...next[i], start: end.subtract(next[i].span - 1, 'day'), end };
      cursor = next[i].start;
    }
  }

  // 첫 잠금부터(잠금이 없으면 처음부터) 순방향
  let cursor = firstLocked === -1 ? anchorStart : null;
  for (let i = firstLocked === -1 ? 0 : firstLocked; i < next.length; i += 1) {
    if (next[i].locked) {
      cursor = next[i].end.add(1, 'day');
      continue;
    }
    const start = cursor ?? next[i].start;
    const end = start.add(next[i].span - 1, 'day');
    next[i] = { ...next[i], start, end };
    cursor = end.add(1, 'day');
  }
  return next;
};

const LockerAttachDateModal = ({
  open,
  onClose,
  onConfirm,
  lockerNumber,
  // 대상 자리에 이미 있는 이용권들
  spotPasses = [],
  // 이 자리로 옮겨오는 이용권들
  movingPasses = [],
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [anchorStart, setAnchorStart] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [showExpiredNotice, setShowExpiredNotice] = useState(false);
  const [overIndex, setOverIndex] = useState(null);
  // 드롭 핸들러가 같은 틱의 오래된 state 를 읽지 않도록 ref 로도 들고 있는다
  const dragIndexRef = useRef(null);

  // 열릴 때 초기 배치 — 기존 이용권은 원래 기간, 옮기는 이용권은 그 뒤로 이어 붙인다
  const initialRows = useMemo(() => {
    const toRow = (lp, origin) => {
      // 만료·중지는 순서 재배치 대상이 아니다.
      //  - 만료: 지난 기록이라 미래로 옮기면 되살아난다. 기간을 고쳐 살릴 수 있게 잠금 해제는 허용한다.
      //  - 중지: 중지한 만큼 종료일을 미뤄둔 상태라 자리 사정으로 또 밀면 중지 구간과 어긋난다.
      //    여기서는 절대 바뀌면 안 되고, 바꿔야 하면 기간 변경으로만 한다(해제 불가).
      const expired = !!lp.expired;
      const stopped = lp.status === 'STOPPED';
      return {
        id: lp.memberLockerPassId,
        name: lp.productName,
        origin,
        expired,
        // 만료는 지난 기록이라 기본 잠금이다. 다만 기간을 유효로 고쳐 되살리려면 풀 수 있어야 한다.
        locked: expired,
        span: spanOf(lp.startDate, lp.expireDate),
        start: dayjs(lp.startDate).startOf('day'),
        end: dayjs(lp.expireDate).startOf('day'),
      };
    };
    const spotRows = spotPasses.map(lp => toRow(lp, 'spot'))
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());
    const movingRows = movingPasses.map(lp => toRow(lp, 'moving'))
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());
    if (spotRows.length === 0) return movingRows;

    let cursor = spotRows[spotRows.length - 1].end.add(1, 'day');
    const chained = movingRows.map(row => {
      // 만료는 지난 기록이라 옮기기만 하고 기간은 그대로 둔다. 다시 잡으면 유효로 되살아난다.
      if (row.expired || row.stopped) return row;
      const start = cursor;
      const end = start.add(row.span - 1, 'day');
      cursor = end.add(1, 'day');
      return { ...row, start, end };
    });
    return [...spotRows, ...chained];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotPasses, movingPasses]);

  useEffect(() => {
    if (!open) {
      setRows([]);
      setExpandedId(null);
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    setRows(initialRows);
    setAnchorStart(initialRows[0]?.start ?? null);
    setExpandedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyRows = nextRows => setRows(rechain(nextRows, anchorStart));

  const moveRow = (from, to) => {
    if (to < 0 || to >= rows.length || from === to) return;
    const next = [...rows];
    const [picked] = next.splice(from, 1);
    next.splice(to, 0, picked);
    applyRows(next);
  };

  const toggleLock = id =>
    applyRows(
      rows.map(row => (row.id === id && !row.lockFixed ? { ...row, locked: !row.locked } : row))
    );

  const editRow = (index, patch) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    next[index] = { ...next[index], span: spanOf(next[index].start, next[index].end) };
    // 직접 고친 행은 그 기간을 기준으로 두고 뒤를 다시 잇는다
    let cursor = next[index].end.add(1, 'day');
    for (let i = index + 1; i < next.length; i += 1) {
      if (next[i].locked) {
        cursor = next[i].end.add(1, 'day');
        continue;
      }
      const start = cursor;
      const end = start.add(next[i].span - 1, 'day');
      next[i] = { ...next[i], start, end };
      cursor = end.add(1, 'day');
    }
    setRows(next);
  };

  const resetToAuto = () => {
    setRows(initialRows);
    setAnchorStart(initialRows[0]?.start ?? null);
  };

  const rangesValid =
    rows.length > 0 && rows.every(row => row.start.isValid() && row.end.isValid() && !row.end.isBefore(row.start, 'day'));
  const overlapping = rows.some((row, i) =>
    rows.some((other, j) => j !== i && !row.start.isAfter(other.end, 'day') && !other.start.isAfter(row.end, 'day'))
  );

  const period = row => `${row.start.format('YYYY-MM-DD')} ~ ${row.end.format('YYYY-MM-DD')} (${row.span}${t('일')})`;
  const submitRows = () => {
    setShowExpiredNotice(false);
    onConfirm(
      rows.map(row => ({
        memberLockerPassId: row.id,
        startDate: row.start.toDate(),
        expireDate: row.end.toDate(),
      }))
    );
  };

  // 저장하면 자리에서 빠질 이용권 — 종료일이 지났는데 같은 자리에 살아 있는 이용권이 있는 경우.
  // 서버가 "유효가 있는 자리의 만료는 미배정" 규칙으로 링크를 떼므로 미리 알린다.
  // 판정은 원래 상태가 아니라 지금 큐의 날짜로 한다 — 잠금을 풀어 기간을 고쳤으면 살아남는다.
  // 중지는 종료일이 지났어도 서버가 만료로 보지 않는다(자리에서 빼지 않는다).
  // 여기서도 같은 기준을 써야 안내와 실제 결과가 어긋나지 않는다.
  const today = dayjs().startOf('day');
  const isDropped = row => !row.stopped && row.end.isBefore(today, 'day');
  const hasLiving = rows.some(row => !isDropped(row));
  const droppedExpired = hasLiving ? rows.filter(isDropped) : [];

  const toneOf = row => (row.locked ? ROW_TONE.locked : ROW_TONE[row.origin]);

  const movingCount = rows.filter(row => row.origin === 'moving').length;
  const title = movingCount > 1
    ? `${t('이용권')} ${movingCount}${t('개를')} ${lockerNumber}${t('번 라커로 옮기기')}`
    : `${rows.find(row => row.origin === 'moving')?.name || t('이용권')}${t('를')} ${lockerNumber}${t('번 라커로 옮기기')}`;

  return (
    <>
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 480, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 48px)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          bgcolor: '#fff', borderRadius: '12px', boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
          boxSizing: 'border-box',
        }}>
        {/* 헤더 — 다른 모달과 같은 검정 바 */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            height: 48, p: '12px 24px', boxSizing: 'border-box', bgcolor: '#262626', flexShrink: 0,
          }}>
          <Box component="span" sx={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{title}</Box>
          <Box
            component="button"
            type="button"
            aria-label={t('닫기')}
            onClick={onClose}
            sx={{
              width: 24, height: 24, p: 0, border: 'none', background: 'none', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              '&:hover': { opacity: 0.7 },
            }}>
            <Close sx={{ width: 20, height: 20 }} />
          </Box>
        </Box>

        <Box sx={{ p: '20px 26px 24px', overflowY: 'auto' }}>
        <Box sx={{ fontSize: 12, color: '#8C959A' }}>
          {t('손잡이를 끌어 순서를 바꾸면 기간이 다시 이어 붙습니다. 자물쇠를 잠그면 그 기간은 고정됩니다.')}
        </Box>

        <Box sx={{ mt: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {rows.map((row, index) => {
            const tone = toneOf(row);
            const expanded = expandedId === row.id;
            const isDragging = dragIndex === index;
            const isOver = overIndex === index && dragIndex !== null && dragIndex !== index;
            return (
              <Box
                key={row.id}
                draggable
                onDragStart={() => {
                  dragIndexRef.current = index;
                  setDragIndex(index);
                }}
                onDragOver={e => {
                  e.preventDefault();
                  setOverIndex(index);
                }}
                onDrop={e => {
                  e.preventDefault();
                  if (dragIndexRef.current !== null) moveRow(dragIndexRef.current, index);
                  dragIndexRef.current = null;
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                onDragEnd={() => {
                  dragIndexRef.current = null;
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                sx={{
                  border: tone.border, borderRadius: '10px', background: tone.bg, overflow: 'hidden',
                  opacity: isDragging ? 0.45 : 1,
                  boxShadow: isOver ? `0 0 0 2px ${tone.accent}` : 'none',
                  transition: 'box-shadow 160ms ease, opacity 160ms ease',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '10px 12px' }}>
                  <Box
                    tabIndex={0}
                    role="button"
                    aria-label={t('순서 이동 — 위/아래 방향키')}
                    onKeyDown={e => {
                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        moveRow(index, index - 1);
                      }
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        moveRow(index, index + 1);
                      }
                    }}
                    sx={{ display: 'inline-flex', borderRadius: '6px', '&:focus-visible': { outline: `2px solid ${tone.accent}` } }}>
                    <DragHandle color={tone.accent} />
                  </Box>

                  <Box
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                    sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 13.5, fontWeight: 700, color: '#262626' }}>
                      {row.name || t('라커 이용권')}
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10.5, fontWeight: 700, color: tone.accent,
                          background: '#fff', border: `1px solid ${tone.accent}33`,
                          borderRadius: '5px', p: '1px 6px',
                        }}>
                        {row.origin === 'moving' ? t('옮기는 중') : t('기존')}
                      </Box>
                    </Box>
                    <Box sx={{ fontSize: 12, fontWeight: 700, color: tone.accent, mt: '3px' }}>
                      {row.start.isValid() && row.end.isValid() && period(row)}
                    </Box>
                  </Box>

                  <Tooltip title={row.locked ? t('기간 고정 해제') : t('기간 고정')} arrow>
                    <Box
                      component="button"
                      type="button"
                      aria-label={row.locked ? t('기간 고정 해제') : t('기간 고정')}
                      aria-pressed={row.locked}
                      onClick={() => toggleLock(row.id)}
                      sx={{
                        width: 32, height: 32, flexShrink: 0, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        border: row.locked ? `1px solid ${tone.accent}` : '1px solid transparent',
                        borderRadius: '8px', background: row.locked ? '#fff' : 'transparent',
                        transition: 'background 160ms ease, border-color 160ms ease',
                        '&:hover': { background: '#fff' },
                        '&:focus-visible': { outline: `2px solid ${tone.accent}` },
                      }}>
                      <LockIcon locked={row.locked} color={tone.accent} />
                    </Box>
                  </Tooltip>

                  <Box
                    component="span"
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                    sx={{ fontSize: 11.5, fontWeight: 700, color: '#0F7F85', flexShrink: 0, cursor: 'pointer' }}>
                    {expanded ? `${t('닫기')} ▲` : `${t('기간 지정')} ▼`}
                  </Box>
                </Box>

                {expanded && (
                  <Box sx={{ borderTop: '1px solid #E7ECED', background: '#fff', p: '13px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box component="span" sx={{ width: 44, flexShrink: 0, fontSize: 11.5, color: '#737373' }}>
                          {t('시작일')}
                        </Box>
                        <DatePicker
                          disabled={row.locked}
                          inputFormat="yyyy-MM-dd"
                          value={row.start.toDate()}
                          onChange={date => {
                            if (!date || !dayjs(date).isValid()) return;
                            const start = dayjs(date).startOf('day');
                            editRow(index, { start, end: start.add(row.span - 1, 'day') });
                          }}
                          renderInput={params => <TextField size="small" sx={dateFieldSx} {...params} />}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box component="span" sx={{ width: 44, flexShrink: 0, fontSize: 11.5, color: '#737373' }}>
                          {t('종료일')}
                        </Box>
                        <DatePicker
                          disabled={row.locked}
                          inputFormat="yyyy-MM-dd"
                          value={row.end.toDate()}
                          onChange={date => {
                            if (!date || !dayjs(date).isValid()) return;
                            editRow(index, { end: dayjs(date).startOf('day') });
                          }}
                          renderInput={params => <TextField size="small" sx={dateFieldSx} {...params} />}
                        />
                      </Box>
                    </LocalizationProvider>
                    <Box component="span" sx={{ fontSize: 11.5, color: '#8C959A' }}>
                      {row.locked
                        ? t('기간이 고정된 이용권입니다. 자물쇠를 풀면 수정할 수 있어요.')
                        : `${t('이용 일수')} ${row.span}${t('일')} · ${t('시작일을 바꾸면 뒤 순서가 따라 이동합니다')}`}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {overlapping && (
          <Box sx={{ mt: '14px', background: 'rgba(238,115,90,0.09)', borderRadius: '8px', p: '10px 14px', fontSize: 12.5, color: '#8A3D26' }}>
            {t('이용권끼리 기간이 겹칩니다. 고정을 풀거나 날짜를 조정해 주세요.')}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mt: '18px' }}>
          <Button
            onClick={resetToAuto}
            sx={{
              height: 44, px: '18px', borderRadius: '8px', fontSize: 14, fontWeight: 600,
              color: '#0F7F85', border: '1px solid #0F7F85', bgcolor: '#fff',
              '&:hover': { bgcolor: 'rgba(15,127,133,0.06)', borderColor: '#0C666B' },
            }}>
            {t('초기화')}
          </Button>
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Button
              onClick={onClose}
              sx={{
                height: 44, px: '24px', minWidth: 100, borderRadius: '8px',
                fontSize: 15, fontWeight: 500, color: '#262626', bgcolor: '#e5e5e5',
                '&:hover': { bgcolor: '#d9d9d9' },
              }}>
              {t('취소')}
            </Button>
            <Button
              variant="contained"
              disabled={!rangesValid || overlapping}
              onClick={() => (droppedExpired.length > 0 ? setShowExpiredNotice(true) : submitRows())}
              sx={{
                height: 44, px: '24px', minWidth: 100, borderRadius: '8px',
                fontSize: 15, fontWeight: 600, bgcolor: '#0F7F85',
                '&:hover': { bgcolor: '#0D6A6F' },
              }}>
              {t('확인')}
            </Button>
          </Box>
        </Box>
        </Box>
      </Box>
    </Modal>

      <ConfirmModal
        open={showExpiredNotice}
        onClose={() => setShowExpiredNotice(false)}
        onConfirm={submitRows}
        message={`이미 만료된 이용권 ${droppedExpired.length}건은 저장하면 이 자리에서 빠집니다.\n같은 자리에 아직 쓸 수 있는 이용권이 있어 연체로 남겨두지 않습니다.\n유효한 이용권으로 바꾸려면 취소한 뒤 자물쇠를 풀고 기간을 오늘 이후로 바꿔주세요.\n\n${droppedExpired
          .map(row => `${row.name || '라커 이용권'} (${row.start.format('YYYY-MM-DD')} ~ ${row.end.format('YYYY-MM-DD')})`)
          .join('\n')}`}
        highlightText={`${droppedExpired.length}건`}
        highlightColor="#EE735A"
        confirmText="빼고 저장"
        cancelText="취소"
        confirmColor="#0F7F85"
      />
    </>
  );
};

export default LockerAttachDateModal;
